import { Loader2 } from "lucide-react";
import { Modal } from "./Modal";

export function Button({
  as: As = "button",
  variant = "primary",
  size = "md",
  loading = false,
  className = "",
  children,
  ...props
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap";
  const sizes = {
    sm: "px-3 py-1.5 text-body-sm",
    md: "px-4 py-2.5 text-label-md",
    lg: "px-5 py-3 text-body-md",
  };
  const variants = {
    primary: "bg-primary text-primary-on hover:bg-primary-container shadow-sm",
    secondary: "bg-surface-container text-primary hover:bg-surface-container-high",
    ghost: "text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface",
    destructive: "bg-error text-error-on hover:opacity-90",
    outline: "border border-outline-variant text-on-surface hover:bg-surface-container-low",
  };

  return (
    <As className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </As>
  );
}

export function Field({ label, error, hint, children, required }) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-label-md text-on-surface">
        {label}
        {required && <span className="text-error"> *</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-body-sm text-error">{error}</span>}
      {!error && hint && <span className="mt-1 block text-body-sm text-on-surface-variant">{hint}</span>}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full rounded border border-outline-variant bg-surface-container-lowest px-3.5 py-2.5 text-body-md text-on-surface placeholder:text-on-surface-variant/70 outline-none transition focus:border-secondary focus:shadow-glow ${
        props.className ?? ""
      }`}
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`w-full rounded border border-outline-variant bg-surface-container-lowest px-3.5 py-2.5 text-body-md text-on-surface outline-none transition focus:border-secondary focus:shadow-glow ${
        props.className ?? ""
      }`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full rounded border border-outline-variant bg-surface-container-lowest px-3.5 py-2.5 text-body-md text-on-surface placeholder:text-on-surface-variant/70 outline-none transition focus:border-secondary focus:shadow-glow ${
        props.className ?? ""
      }`}
    />
  );
}

export function Card({ className = "", children, ...props }) {
  return (
    <div
      className={`rounded-xl border border-outline-variant/50 bg-surface-container-lowest shadow-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-outline-variant py-16 text-center">
      {Icon && (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-low text-on-surface-variant">
          <Icon size={22} />
        </div>
      )}
      <div>
        <p className="text-body-md font-medium text-on-surface">{title}</p>
        {description && <p className="mt-1 max-w-sm text-body-sm text-on-surface-variant">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`skeleton ${className}`} />;
}

export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="divide-y divide-outline-variant/50">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-6 px-4 py-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "purple", trend }) {
  const tones = {
    purple: "bg-chart-purple/10 text-chart-purple",
    orange: "bg-chart-orange/15 text-chart-orange",
    blue: "bg-chart-blue/15 text-chart-blue",
    green: "bg-chart-green/10 text-chart-green",
    primary: "bg-primary-container/20 text-primary",
    secondary: "bg-secondary-container/50 text-secondary",
    tertiary: "bg-tertiary-container/20 text-tertiary",
    error: "bg-error-container/60 text-error",
  };
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-body-sm text-on-surface-variant">{label}</p>
          <p className="mt-1.5 text-headline-lg text-on-surface">{value}</p>
          {trend && <p className="mt-1 text-body-sm text-secondary">{trend}</p>}
        </div>
        {Icon && (
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}>
            <Icon size={20} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel = "Confirm", loading, danger, children }) {
  return (
    <Modal open={open} onClose={onClose} title={title} description={description} maxWidth="max-w-md">
      {children}
      <div className="mt-5 flex justify-end gap-2.5">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant={danger ? "destructive" : "primary"} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

export function Avatar({ name, size = 36 }) {
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      style={{ width: size, height: size }}
      className="flex shrink-0 items-center justify-center rounded-full bg-secondary-container text-secondary-on-container font-semibold"
    >
      <span style={{ fontSize: size * 0.38 }}>{initials}</span>
    </div>
  );
}
